import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { DrawerSetting, GridColumn } from '@grid/abstractions';
import { TranslateService } from '@ngx-translate/core';
import { SearchSetting } from '@shared/abstraction/grid/search-setting';
import { DataType } from '@shared/helpers/enums/data-type';
import { QueryOptions } from 'odata-query';
import {
  Collection,
  GridItemCreateEvent,
  InvestmentJurisdiction,
} from '../../models';
import { UpsertInvestmentJurisdictionComponent } from '../upsert-investment-jurisdiction/upsert-investment-jurisdiction.component';

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gstar-investment-jurisdictions-grid',
  templateUrl: './investment-jurisdictions-grid.component.html',
})
export class InvestmentJurisdictionsGridComponent implements OnChanges {
  private translateService = inject(TranslateService);

  // Keep a stable selection state across paginator changes.
  private selectedCountryIdsByInvestmentJurisdictionId = new Map<
    number,
    number
  >();

  @Input({ required: true }) source: Collection<InvestmentJurisdiction> = {
    total: 0,
    items: [],
  };

  @Input() query!: Partial<QueryOptions<InvestmentJurisdiction>>;
  @Output() queryChange = new EventEmitter<
    Partial<QueryOptions<InvestmentJurisdiction>>
  >();

  @Output() create = new EventEmitter<
    GridItemCreateEvent<InvestmentJurisdiction>
  >();

  @Output() update = new EventEmitter<
    GridItemCreateEvent<InvestmentJurisdiction>
  >();

  @Output() exportDownload = new EventEmitter<number[]>();
  @Output() exportDownloadAll = new EventEmitter<void>();

  onExportDownload(_selectedRows: InvestmentJurisdiction[]): void {
    this.syncSelectionFromVisibleRows();

    const countryIds = [
      ...new Set(this.selectedCountryIdsByInvestmentJurisdictionId.values()),
    ];
    this.exportDownload.emit(countryIds);
  }

  onExportDownloadAll(): void {
    this.exportDownloadAll.emit();
  }

  drawerSetting: DrawerSetting<InvestmentJurisdiction> = {
    create: {
      title: this.translateService.instant(
        'admin.grid.createInvestmentJurisdictionTitle'
      ),
      component: UpsertInvestmentJurisdictionComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.create'),
          command: (closeFn, item): void => {
            if (item.isValid) {
              this.create.emit({ item: item.model!, onSuccess: closeFn });
            }
          },
          isPrimary: true,
        },
      ],
    },
    edit: {
      title: this.translateService.instant(
        'admin.grid.updateInvestmentJurisdictionTitle'
      ),
      component: UpsertInvestmentJurisdictionComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.update'),
          command: (closeFn, item): void => {
            if (item.isValid) {
              this.update.emit({ item: item.model!, onSuccess: closeFn });
            }
          },
          isPrimary: true,
        },
      ],
    },
  };

  headers: GridColumn[] = [
    {
      label: this.translateService.instant('investmentJurisdiction.name'),
      key: 'country.name',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('investmentJurisdiction.region'),
      key: 'region',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('investmentJurisdiction.modified'),
      key: 'modified',
      dataType: DataType.Date,
      isSortable: true,
    },
    {
      label: this.translateService.instant('investmentJurisdiction.isActive'),
      key: 'isActive',
      dataType: DataType.Boolean,
      isSortable: true,
    },
    {
      label: this.translateService.instant('investmentJurisdiction.isDisplay'),
      key: 'isDisplay',
      dataType: DataType.Boolean,
      isSortable: true,
    },
    {
      label: this.translateService.instant('investmentJurisdiction.export'),
      key: 'isExportSelected',
      dataType: DataType.Boolean,
      isSortable: false,
      renderBooleanAsCheckbox: true,
      booleanCheckboxInteractive: true,
    },
  ];

  searchSetting: SearchSetting<InvestmentJurisdiction> = {
    filtersLogicalOperator: 'or',
    filters: [
      {
        dataKey: 'country/name' as keyof InvestmentJurisdiction,
        operator: 'contains',
      },
    ],
  };

  queryHandler(
    investmentJurisdictionQuery: Partial<QueryOptions<InvestmentJurisdiction>>
  ): void {
    this.syncSelectionFromVisibleRows();
    this.queryChange.emit(investmentJurisdictionQuery);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['source']) {
      this.applySelectionToVisibleRows();
    }
  }

  private syncSelectionFromVisibleRows(): void {
    this.source.items.forEach(row => {
      const investmentJurisdictionId = row.investmentJurisdictionId;
      const countryId = row.country?.countryId;

      if (
        row.isExportSelected &&
        countryId !== undefined &&
        countryId !== null
      ) {
        this.selectedCountryIdsByInvestmentJurisdictionId.set(
          investmentJurisdictionId,
          countryId
        );
        return;
      }

      this.selectedCountryIdsByInvestmentJurisdictionId.delete(
        investmentJurisdictionId
      );
    });
  }

  private applySelectionToVisibleRows(): void {
    this.source.items.forEach(row => {
      row.isExportSelected =
        this.selectedCountryIdsByInvestmentJurisdictionId.has(
          row.investmentJurisdictionId
        );
    });
  }
}
